import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { CalendarIcon, X, ChevronLeft, ChevronRight, ChevronsUpDown, Search } from "lucide-react";
import { format } from "date-fns";
import { th } from "date-fns/locale";
import { cn } from "@/lib/utils";

const TEACHER_TIERS = [
  { value: "Tier 1", label: "Tier 1" },
  { value: "Tier 2", label: "Tier 2" },
  { value: "Tier 3", label: "Tier 3" },
  { value: "Tier 4", label: "Tier 4" },
  { value: "CUSTOM", label: "Custom" },
];

const THAI_MONTHS = [
  "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
  "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
];

interface StudentStatsFiltersProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  selectedTiers: string[];
  selectedTeachers: string[];
  teacherOptions: { code: string; nickname: string | null }[];
  dateMode: 'month' | 'day';
  selectedMonth: number;
  selectedYear: number;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  onTierChange: (tiers: string[]) => void;
  onTeacherChange: (teachers: string[]) => void;
  onDateModeChange: (mode: 'month' | 'day') => void;
  onMonthChange: (month: number, year: number) => void;
  onClear: () => void;
}

export const StudentStatsFilters: React.FC<StudentStatsFiltersProps> = ({
  startDate,
  endDate,
  selectedTiers,
  selectedTeachers,
  teacherOptions,
  dateMode,
  selectedMonth,
  selectedYear,
  onStartDateChange,
  onEndDateChange,
  onTierChange,
  onTeacherChange,
  onDateModeChange,
  onMonthChange,
  onClear,
}) => {
  const [teacherSearch, setTeacherSearch] = useState("");

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      onMonthChange(12, selectedYear - 1);
    } else {
      onMonthChange(selectedMonth - 1, selectedYear);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      onMonthChange(1, selectedYear + 1);
    } else {
      onMonthChange(selectedMonth + 1, selectedYear);
    }
  };

  const toggleTier = (tier: string) => {
    if (selectedTiers.includes(tier)) {
      onTierChange(selectedTiers.filter(t => t !== tier));
    } else {
      onTierChange([...selectedTiers, tier]);
    }
  };

  const toggleTeacher = (code: string) => {
    if (selectedTeachers.includes(code)) {
      onTeacherChange(selectedTeachers.filter(t => t !== code));
    } else {
      onTeacherChange([...selectedTeachers, code]);
    }
  };

  const filteredTeachers = teacherOptions.filter(t => {
    if (!teacherSearch.trim()) return true;
    const s = teacherSearch.trim().toLowerCase();
    return t.code.toLowerCase().includes(s) || t.nickname?.toLowerCase().includes(s);
  });

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date Mode Toggle */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">เดือน</span>
        <Switch
          checked={dateMode === 'day'}
          onCheckedChange={(checked) => onDateModeChange(checked ? 'day' : 'month')}
        />
        <span className="text-sm text-muted-foreground">วัน</span>
      </div>

      {dateMode === 'month' ? (
        /* Month Picker */
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={handlePrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-3 py-2 text-sm font-medium min-w-[160px] text-center border rounded-md bg-background">
            {THAI_MONTHS[selectedMonth - 1]} {selectedYear + 543}
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        /* Day Pickers */
        <>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">ตั้งแต่:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[160px] justify-start text-left font-normal",
                    !startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "dd MMM yyyy", { locale: th }) : "เลือกวัน"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={onStartDateChange}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">ถึง:</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-[160px] justify-start text-left font-normal",
                    !endDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "dd MMM yyyy", { locale: th }) : "เลือกวัน"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={onEndDateChange}
                  initialFocus
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </>
      )}

      {/* Tier Multi-Select */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Tier:</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="min-w-[130px] justify-between">
              {selectedTiers.length === 0 ? "ทั้งหมด" : `${selectedTiers.length} เลือก`}
              <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[180px] p-2" align="start">
            <div className="space-y-1">
              {TEACHER_TIERS.map((tier) => (
                <label
                  key={tier.value}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted/50 cursor-pointer text-sm"
                >
                  <Checkbox
                    checked={selectedTiers.includes(tier.value)}
                    onCheckedChange={() => toggleTier(tier.value)}
                  />
                  {tier.label}
                </label>
              ))}
              {selectedTiers.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-1 text-xs"
                  onClick={() => onTierChange([])}
                >
                  ล้าง
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Teacher Multi-Select */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">ครู:</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="min-w-[150px] justify-between">
              {selectedTeachers.length === 0 ? "ทั้งหมด" : `${selectedTeachers.length} คน`}
              <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-2" align="start">
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="ค้นหาครู..."
                  value={teacherSearch}
                  onChange={(e) => setTeacherSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              <div className="max-h-[240px] overflow-y-auto space-y-0.5">
                {filteredTeachers.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">ไม่พบครู</p>
                ) : (
                  filteredTeachers.map((teacher) => (
                    <label
                      key={teacher.code}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-sm hover:bg-muted/50 cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={selectedTeachers.includes(teacher.code)}
                        onCheckedChange={() => toggleTeacher(teacher.code)}
                      />
                      <span className="truncate">
                        {teacher.nickname || teacher.code}
                        {teacher.nickname && (
                          <span className="text-muted-foreground ml-1">({teacher.code})</span>
                        )}
                      </span>
                    </label>
                  ))
                )}
              </div>
              {selectedTeachers.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full text-xs"
                  onClick={() => onTeacherChange([])}
                >
                  ล้างทั้งหมด
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Clear All Button */}
      <Button variant="ghost" size="sm" onClick={onClear} className="text-muted-foreground">
        <X className="h-4 w-4 mr-1" />
        ล้าง
      </Button>
    </div>
  );
};
